import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { entrar, guardarSesion, pedirPasswordNueva } from '../api';
import { useTema } from '../tema';
import BotonTema from '../componentes/BotonTema';

/**
 * La entrada al portal.
 *
 * Se entra con el correo, que es el mismo dato por el que WF le escribe. Debajo está el
 * "olvidé mi contraseña", que manda una temporal a ese correo.
 *
 * Se ve igual que el login del sistema interno —mismo logo, mismo fondo azul, mismas clases—
 * porque para el cliente es la misma casa. Lo único que cambia es la bajada: "Portal de
 * clientes" en vez de "Sistema de Gestión de Trámites".
 *
 * Un detalle que parece un descuido y no lo es: cuando alguien pide contraseña nueva, la
 * pantalla contesta lo mismo exista o no exista esa cuenta. Si dijera "ese correo no está
 * registrado", cualquiera podría averiguar quiénes son clientes de WF probando correos.
 */
export default function Ingresar() {
  const navegar = useNavigate();
  const { tema, alternar } = useTema();

  const [correo, setCorreo] = useState('');
  const [password, setPassword] = useState('');
  const [entrando, setEntrando] = useState(false);
  const [error, setError] = useState('');

  const [modoOlvide, setModoOlvide] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [avisoOlvide, setAvisoOlvide] = useState('');

  const enviarLogin = async (evento) => {
    evento.preventDefault();
    setError('');
    setEntrando(true);
    try {
      const { data } = await entrar(correo.trim(), password);
      guardarSesion(data);
      navegar(data.debeCambiarPassword ? '/cambiar-contrasena' : '/', { replace: true });
    } catch (err) {
      // Siempre el mismo texto, pase lo que pase por dentro. Un mensaje que cambie según el
      // motivo -"esa cuenta está desactivada", "ese correo no existe"- le va contando a quien
      // prueba desde fuera qué correos son de clientes de WF y cuáles están vivos.
      //
      // La única excepción es el 429: ahí la persona TIENE que leer que solo debe esperar, o se
      // queda probando la contraseña correcta sin entender por qué no entra. Y no delata nada,
      // porque el freno cuenta cualquier correo que se escriba, exista o no.
      const estado = err?.response?.status;
      setError(estado === 429
        ? (err.response.data?.message || 'Demasiados intentos. Espere unos minutos.')
        : estado === 401 || estado === 400
          ? 'Correo o contraseña incorrectos.'
          : 'No se pudo entrar. Intente de nuevo en un momento.');
    } finally {
      setEntrando(false);
    }
  };

  const enviarOlvide = async (evento) => {
    evento.preventDefault();
    setEnviando(true);
    setAvisoOlvide('');
    try {
      const { data } = await pedirPasswordNueva(correo.trim());
      setAvisoOlvide(data.mensaje);
    } catch {
      // Ni un fallo cambia el mensaje: la diferencia también delataría qué correos existen.
      setAvisoOlvide('Si ese correo tiene una cuenta en el portal, le llegará una contraseña temporal.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <div style={{ position: 'fixed', top: 16, right: 16 }}>
        <BotonTema tema={tema} alternar={alternar} />
      </div>

      <div className="login-card">
        <div className="login-logo">
          <img
            src="/LOGO-WF-long-color.png"
            alt="WF Aduanas y Logística"
            style={{ width: 260, maxWidth: '100%', height: 'auto', marginBottom: 6 }}
          />
          <p style={{ color: 'var(--gris-texto)', fontSize: 13, marginTop: 0 }}>
            Portal de clientes
          </p>
        </div>

        {!modoOlvide ? (
          <form onSubmit={enviarLogin}>
            <div className="form-group">
              <label className="form-label required" htmlFor="correo">Correo</label>
              <input
                id="correo" className="form-control" type="email" autoComplete="username"
                value={correo} onChange={(e) => setCorreo(e.target.value)}
                placeholder="sucorreo@empresa.com" required autoFocus
              />
            </div>
            <div className="form-group">
              <label className="form-label required" htmlFor="clave">Contraseña</label>
              <input
                id="clave" className="form-control" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required
              />
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>⚠️ {error}</div>}

            <button className="btn btn-primary" type="submit" disabled={entrando}
              style={{ width: '100%' }}>
              {entrando ? 'Entrando…' : 'Entrar'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" className="btn-enlace"
                onClick={() => { setModoOlvide(true); setError(''); }}>
                Olvidé mi contraseña
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={enviarOlvide}>
            <p style={{ fontSize: 13, color: 'var(--gris-texto)', marginBottom: 16, lineHeight: 1.5 }}>
              Escriba el correo de su cuenta y le enviamos una contraseña temporal. Al entrar con
              ella el sistema le pedirá que elija una suya.
            </p>
            <div className="form-group">
              <label className="form-label required" htmlFor="correo-olvide">Correo</label>
              <input
                id="correo-olvide" className="form-control" type="email"
                value={correo} onChange={(e) => setCorreo(e.target.value)}
                placeholder="sucorreo@empresa.com" required autoFocus
              />
            </div>

            {avisoOlvide && (
              <div className="alert alert-success" style={{ marginBottom: 14 }}>{avisoOlvide}</div>
            )}

            <button className="btn btn-primary" type="submit" disabled={enviando}
              style={{ width: '100%' }}>
              {enviando ? 'Enviando…' : 'Enviarme una contraseña temporal'}
            </button>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button type="button" className="btn-enlace"
                onClick={() => { setModoOlvide(false); setAvisoOlvide(''); }}>
                Volver
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
